import React, { useEffect, useRef, useState } from 'react';
import { SettlementItemDetails } from './SettlementItemDetails';
import { FeeList } from './FeeList';
import { IFeeEditLayout } from './IFeeEditLayout';
import { Form, Formik } from 'formik';
import { ISettlementItemForm } from 'types/order/fees/ICreateSettlementItemForm';
import { lineItemFormSchema } from '../NewFeesSchema';
import { ISectionDefinitionData } from 'types/order/fees/ISectionDefinition';
import { ISectionData } from 'types/order/fees/ISectionData';
import { Validation } from '../../validation/Validation';
import { useGetOptionsFromSectionDefinition } from '../useGetCurrentOptions';
import { DisbursementRollupProvider } from '../disbursements/DisbursementRollupContext';
import { LineItemSaveManager } from '../LineItemSaveManager';
import { ModalTarget } from './ModalTarget.Constant';
import { useLineItemSave } from '../useLineItemSave';
import { useSelectCurrentOrderId } from 'selectors/order/selectCurrentOrder';
import { assertDefined } from 'utilities/Assertions';
import { removeHiddenFeeProperties } from './removeHiddenFeeProperties';

export interface IEditFormProps extends IFeeEditLayout {
  lineItemForm: ISettlementItemForm;
  section?: ISectionData;
  sectionDefinition: ISectionDefinitionData;
}

export const EditForm: React.FC<IEditFormProps> = ({ layout, lineItemForm, section, sectionDefinition }) => {
  const item = useRef<HTMLDivElement>(null);
  const [lineItemId, setLineItemId] = useState<string | undefined>(lineItemForm.settlementStatementItemId);
  const isUpdate = !!lineItemId;

  const options = useGetOptionsFromSectionDefinition(lineItemForm, sectionDefinition);
  const isReadOnly = !!lineItemId && !options?.[0]?.editable;

  const updateDisplay = isReadOnly ? 'View Settlement Item' : 'Edit Settlement Item';
  const title = isUpdate ? updateDisplay : 'Add Settlement Item';
  const buttonText = isUpdate ? 'Update' : 'Add';
  useEffect(() => {
    if (item.current?.scrollIntoView) {
      const previousTop = window.document.documentElement.scrollTop || document.body.scrollTop;
      item.current.scrollIntoView({ behavior: 'instant', block: 'start' });
      return () => {
        if (previousTop > 0) {
          window.scrollTo({ behavior: 'instant', top: previousTop });
        }
      };
    }
    return () => {};
  }, []);
  const orderId = useSelectCurrentOrderId();
  assertDefined(orderId);
  const lineItemSave = useLineItemSave({ orderId });
  return (
    <Formik
      initialValues={lineItemForm}
      onSubmit={async (values) => {
        const filteredValues = removeHiddenFeeProperties(values, options);
        const result = await lineItemSave(filteredValues);
        if (typeof result === 'string') {
          setLineItemId(result);
        }
      }}
      validateOnChange
      enableReinitialize
      validationSchema={lineItemFormSchema}
    >
      {({ dirty, submitForm, values, errors, submitCount }) => (
        <Form>
          <DisbursementRollupProvider>
            <LineItemSaveManager
              actionText={buttonText}
              lineItemId={lineItemId ?? sectionDefinition.statementSectionDefinitionId}
              container={item.current}
              dirty={dirty}
              submitForm={submitForm}
            >
              {({ SaveButtons }) => (
                <>
                  <div ref={item} className={'relative scroll-mt-40'}>
                    <div className={'bg-gray-100 p-4'}>
                      <div className={'mb-4'}>
                        <div className={'text-xl font-semibold'}>{title}</div>
                        <div className={'text-sm font-light'}>
                          Please note: depending on the item category you may be required to add a document.
                        </div>
                      </div>
                      {section && sectionDefinition && (
                        <Validation
                          currentLineItem={values}
                          sections={[section]}
                          sectionDefinitions={[sectionDefinition]}
                        />
                      )}
                      <SettlementItemDetails
                        lineItem={values}
                        dirty={dirty}
                        isReadOnly={isReadOnly}
                        options={options}
                      />
                      <FeeList
                        layout={layout}
                        lineItem={values}
                        options={options}
                        isDirty={!!values.isNew || dirty}
                        hasErrors={submitCount > 0 && Object.keys(errors).length > 0}
                      />
                      {SaveButtons}
                    </div>
                  </div>
                  {/** Modal target is needed because the sticky cards above screw with z-index things so modals/select/date dropdowns need to be adjusted to work.
                   * Modals have no easy way of working, so instead we shunt them out of where they would be to here. Still inside the card (OutsideclickHandler approved!).
                   */}
                  <div id={ModalTarget}></div>
                </>
              )}
            </LineItemSaveManager>
          </DisbursementRollupProvider>
        </Form>
      )}
    </Formik>
  );
};
